import classNames from "classnames";
import { useState, useCallback, useEffect, useRef } from "react";
import { Col, Row, Button } from "react-bootstrap";
import { RootState } from "redux/store";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getGenericCategories } from "redux/slices/GenericCategories";
import { updatePreferredBrandField } from "redux/slices/PreferredBrands";
import { GenericCategoriesStocks } from "popups/GenericCategoriesStocks/GenericCategoriesStocks";
import { Table, CommonColumnProperties, ImportWizard, Modal} from "components";
import { ValidationState } from "components/ImportWizard/ImportWizardTypes";
import { getPreferredBrandDetails } from "redux/slices/PreferredBrands";
import { runBatchWorkers } from "components/WebWorkers/ZWebWorker";
import { PreferredBrandsImportWorkerData, PreferredBrandsImportWorkerResponseData } from "./preferredBrandsImportWorker";
import { AgGridReact } from "ag-grid-react";
import BaseCellRenderer from "components/AgGrid/CellRenderers/BaseCellRenderer";
import { getPreferredBrandsGenericCategoryConfig } from "TableConfigs/PreferredBrandsGenericCategoryConfig";
import { AgGridToolbar } from "components/AgGrid/AgGridToolbar";
import { ZTooltip } from "components/ZTooltip";
import { Z } from "models/common/components";
import { ZIconType } from "helpers/utility/iconMap";
import { getPreferredBrandsStockLevelConfig } from "TableConfigs/PreferredBrandsStockLevelConfig";

export function Stock() {
    const dispatch = useAppDispatch();
    const genericCategoryTableRef = useRef<any>(null);
    const [showPopup, setShowPopup] = useState(false);
    const [importWizardPopup, setImportWizardPopup] = useState(false);
    const [importPercentage, setImportPercentage] = useState<number>(0);
    const [genericCategoriesloading, setGenericCategoriesLoading] = useState(false);
    const [genericCategoriesStock, setGenericCategoriesStock] = useState<any[]>([]);
    const preferredBrands = useAppSelector((state: RootState) => state.preferredBrands);
    const genericCategories = useAppSelector((state: RootState) => state.genericCategories);
    const user = useAppSelector((state: RootState) => state.logedinUser);

    const [selectedGenericCategoriesId, setSelectedGenericCategoriesId] = useState<string | null>(null);
    const genericCategoriesStocksGridRef = useRef<AgGridReact>(null);
    const [showDeleteCategory, setShowDeleteCategory] = useState<boolean>(false);
    const [showDeleteStock, setShowDeleteStock] = useState<boolean>(false);
    const [forcRefresh, setForceRefresh] = useState<boolean>(false);

    useEffect(() => {
        if (preferredBrands.genericCategories.length == 0)
            return;

        if (selectedGenericCategoriesId == null){
            setSelectedGenericCategoriesId(preferredBrands.genericCategories[0].genericCategoryId);
            return;
        }
        const selectedCategories = preferredBrands.genericCategories.filter(x=>x.genericCategoryId == selectedGenericCategoriesId)
        if(selectedCategories.length != 0){
            let cleanData: any[]  = [];
            selectedCategories[0].stocks.forEach((stock: any)=>{
                cleanData.push({
                    name: stock.name,
                    rank: stock.rank,
                    genericCategoryId: stock.genericCategoryId,
                    productId: stock.productId
                });
            })
            setGenericCategoriesStock(cleanData);
        } else {
            setGenericCategoriesStock([]);
        }
        
    }, [preferredBrands.genericCategories, selectedGenericCategoriesId ]);

    useEffect(() => {
        dispatch(getGenericCategories({})).then((res) => {
            handleUpdate("mainGenericCategoriesTable", res.data);
            if (preferredBrands.stock.length > 0) genericCategoriesDataProsess();
            setGenericCategoriesLoading(true);
        });
    }, []);

    useEffect(() => {
        genericCategoriesDataProsess();
    }, [preferredBrands.stock, genericCategoriesloading]);

    useEffect(() => {
        if (preferredBrands.loading) setGenericCategoriesStock([]);
    }, [preferredBrands.loading]);

    function genericCategoriesDataProsess() {
        if (preferredBrands.mainGenericCategoriesTable.length > 0 && preferredBrands.stock.length > 0) {
            const genericCategoriesTable: any[] = [];
            const genericCategoryIdList: any[] = [...preferredBrands.stock.map((item: any) => item.genericCategoryId)];
            genericCategoryIdList.forEach((item: any) => {
                const genericCategoryItem: any = preferredBrands.mainGenericCategoriesTable.find((category: any) => category.id === item);
                const genericCategoryItemIndex: number = genericCategoriesTable.findIndex((category: any) => category.genericCategoryId === item);
                if (genericCategoryItem && genericCategoryItemIndex === -1) {
                    genericCategoriesTable.push({
                        genericCategoryId: item,
                        genericCategoryName: `${genericCategoryItem.fullName}`,
                        stocks: [
                            ...preferredBrands.stock.filter((stock: any) => stock.genericCategoryId === item),
                        ],
                    });
                }
            });
            handleUpdate("genericCategories", genericCategoriesTable.sort((a, b) => a.genericCategoryName.localeCompare(b.genericCategoryName)));
        }
    }

    async function genericCategoriesOnSave() {
        let selectedGenericCategories: any[] = [...genericCategories.selectedgenericCategoriesStock];
        let genericCategoriesList: any[] = [...preferredBrands.stock];
        const genericCategoriesStock: any[] = [...preferredBrands.stock.map((item: any) => item.productId)];
        let rebuildRank = 0;
        selectedGenericCategories.forEach((item: any) => {
            const currentItem: any = genericCategories.stockList[item];
            if (!genericCategoriesStock.includes(currentItem.id)) {
                rebuildRank= rebuildRank + 1; 
                genericCategoriesList.push({
                    productId: currentItem.id,
                    rank: rebuildRank,
                    genericCategoryId: currentItem.genericCategoryId,
                    name: currentItem.name,
                    dispenseName: currentItem.dispenseName,
                });                
            }
        });
        handleUpdate("stock", genericCategoriesList)
        setSelectedGenericCategoriesId(genericCategoriesList[genericCategoriesList.length - 1].genericCategoryId);
        setShowPopup(false);

    }

    function handleDelete() {
        let rebuiltRows: any[] = [];
        const selectedProductIds = (genericCategoriesStocksGridRef.current?.api.getSelectedRows()!).map((row: any) => row.productId);
        let rebuildRank = 0;
        genericCategoriesStocksGridRef.current?.api.forEachNode((node) => {
            if(selectedProductIds.includes(node.data.productId) == false){
                    rebuildRank= rebuildRank + 1; 
                    node.data.rank = rebuildRank;
                    rebuiltRows.push(node.data);
                }
            }
        );

        const indexForUpdate = preferredBrands.genericCategories.findIndex((x: any) => x.genericCategoryId == selectedGenericCategoriesId);
        const preferredStock = [...preferredBrands.genericCategories];
        preferredStock[indexForUpdate] = { ...preferredStock[indexForUpdate], stocks: [...rebuiltRows] };

        handleUpdate("genericCategories", preferredStock);
    }

    function handleDeleteCategory() {
        let newGenericCategories: any = [];
        if (preferredBrands.selectedCategoryRows.length !== preferredBrands.genericCategories.length) {
           newGenericCategories = preferredBrands.genericCategories.filter((cat)=> !preferredBrands.selectedCategoryRows.includes(cat.genericCategoryId));
        }
        
        handleUpdate("genericCategories", newGenericCategories); 
    }

    function handleUpdate(key: string, value: any) {
        dispatch(updatePreferredBrandField({ key: key, value: value })); 
    }

    const terminateWorkersRef = useRef<Function>();

    async function importPreferredBrands(rows: any) {
        let newStocks: any[] = [];

		const webWorkerData: PreferredBrandsImportWorkerData = {
			data: [...rows],
		};

		const errors: Map<string, string> = new Map<string, string>();

        let updatedValidLines: any[] = [];

        const { runWorkers, terminateWorkers } = runBatchWorkers<PreferredBrandsImportWorkerData, any, PreferredBrandsImportWorkerResponseData>({
            workerData: webWorkerData, 
            createWorker: () => new Worker(new URL('./preferredBrandsImportWorker.ts', import.meta.url)), 
            onWorkerResponse: async (event, responseContext, httpErrors, reject) => {
                if (!responseContext.response.data) {
                    if (httpErrors) {
                        const combinedError = httpErrors.join(", ");
                        // Now using this combined error, construct a { stockName, error } object for each stock and push it onto the errors array
                        responseContext.originalBatchData.forEach((r: any) => {
                            const errorEntry = errors.get(r.__name);
                            errors.set(r.__name ?? "", errorEntry ? errorEntry + ", " + combinedError : combinedError);
                        });
                    }
                } else {
                    updatedValidLines.push(responseContext.response.data.updatedValidLines); // Push the valid lines that the web worker updated into a list that we can then iterate over once all web workers are finished
                }
                
				setImportPercentage((prev) => Math.min(100, prev + ((1 / responseContext.numBatches) * 100)));
            }, 
            onWorkersFinished: async (_) => {
                // Once all web workers are done, we iterate over the newly updated valid lines and perform the necessary logic for getting the new stocks to then update the table with.
                let flattendedLines = updatedValidLines.flat();
                flattendedLines.forEach((stock: any) => {
                    const existingStock = preferredBrands.stock.find((item: any) => item.productId === stock.id);
                    if (!existingStock) {
                        if (stock.genericCategoryId === undefined || stock.genericCategoryId === null) {
                            const errorEntry = errors.get(stock.__name);
                            errors.set(stock.__name ?? "", errorEntry ? errorEntry + ", " + "No Generic Category" : "No Generic Category");
                        } else {
                            newStocks.push({
                                productId: stock.id,
                                rank: 0,
                                genericCategoryId: stock.genericCategoryId,
                                name: stock.__name,
                                dispenseName: stock.__name,
                            });
                        }
                    }
                });
                handleUpdate("stock", [...preferredBrands.stock, ...newStocks]);
                setImportPercentage(0); //Reset the import percentage
            }, 
            onWorkerError: (error, unprocessedData) => {
                unprocessedData.forEach(data => {
                    const errorEntry = errors.get(data.__name);
                    const errorMessage = error.message ?? "Unknown error occurred";
                    errors.set(data.__name ?? "", errorEntry ? errorEntry + ", " + errorMessage : errorMessage);
                })
		    }
        });

        terminateWorkersRef.current = terminateWorkers;
        
        await runWorkers;

        const returnErrors: { stockName: string, error: string }[] = [];

		errors.forEach((value: string, key: string) => {
			returnErrors.push({
				stockName: key,
				error: value,
			})
		});

		return [...returnErrors];
    }

    function WholeTableValidation(rows: CommonColumnProperties[]): CommonColumnProperties[] {
        let temp: CommonColumnProperties[] = [...rows];

        rows.forEach(row => {
            if (row.hasError === undefined || row.hasError === false) { //don"t bother validating bad rows

                if (row.__pde !== undefined && row.__pde !== null) {
                    var otherMatches = temp.filter(i => i.__pde === row.__pde && i !== row);
                    if (otherMatches.length > 0) {
                        row.message = "Duplicate PDE";
                        row.hasError = true;
                        row.validationState = ValidationState.Invalid;
                    }
                }
            }
        });

        return temp;
    }

    function updateGenericStockOrder(){
        let rebuiltRows: any[] = [];        
        genericCategoriesStocksGridRef.current?.api.forEachNode((node) => {rebuiltRows.push(node.data)});

        const indexForUpdat = preferredBrands.genericCategories.findIndex((x: any) => x.genericCategoryId == selectedGenericCategoriesId);
        const preferredStock = [...preferredBrands.genericCategories];
        preferredStock[indexForUpdat] = { ...preferredStock[indexForUpdat], stocks: [...rebuiltRows] };
        handleUpdate("genericCategories", preferredStock);
    }

    return (
        <div className="set-search-with">
            <GenericCategoriesStocks show={showPopup} onClose={() => setShowPopup(false)} onSave={genericCategoriesOnSave} />
            <Row>
                <Col lg={6}>
                    <AgGridToolbar
                        search={true}
                        className="pb-1"
                        handleSubmit={(searchState, filterState) => {
                            genericCategoryTableRef.current?.api.clearRangeSelection();
                            handleUpdate("selectedCategoryRows", []);
                            genericCategoryTableRef.current?.api.setGridOption('quickFilterText', searchState?.keyword);                                            
                        }}
                        searchProps={
                            {totalRows: preferredBrands.genericCategories.length, placeholder: "by Name", hideContains: true}
                        }
                        children={(
                            <div className="d-inline-flex gap-1">
                                { 
                                (preferredBrands.selectedCategoryRows != undefined && preferredBrands.selectedCategoryRows.length != 0) &&									
                                    <Z.Button variant="light" className="h-100" disabled={true} buttonText={preferredBrands.selectedCategoryRows.length+ " Selected"}/>
                                }
                                    <ZTooltip text="Add">
                                    <Z.Button icon={ZIconType.Add} buttonSize="md" onClick={() => setShowPopup(true)}/>
                                    </ZTooltip>
                                <ZTooltip text={
                                    preferredBrands.selectedCategoryRows.length == 0? `Please select a row to delete`:
                                    preferredBrands.selectedCategoryRows.length == preferredBrands.genericCategories.length ? `Delete all rows`:									  
                                    preferredBrands.selectedCategoryRows.length> 0 ? `Delete ${preferredBrands.selectedCategoryRows.length} rows` :                                                    
                                    "Please select a row to delete"}>
                                    <Z.Button icon={ZIconType.Delete} buttonSize="md"  onClick={() => setShowDeleteCategory(true)} 
                                        disabled={preferredBrands.selectedCategoryRows.length < 1} />
                                </ZTooltip>
                                <ZTooltip text="Refresh">
                                    <Z.Button icon={ZIconType.Refresh} buttonSize="md" onClick={() => 
                                       { dispatch(getGenericCategories({})).then((res) => {
                                            handleUpdate("mainGenericCategoriesTable", res.data);
                                            if (preferredBrands.stock.length > 0) genericCategoriesDataProsess();
                                            setGenericCategoriesLoading(true);
                                        })}}
                                    />
                                </ZTooltip>
                            </div>
                        )}
                    />
                    <AgGridReact
                        ref={genericCategoryTableRef}
                        columnDefs={getPreferredBrandsGenericCategoryConfig(selectedGenericCategoriesId)}
                        rowData={preferredBrands.genericCategories}
                        pagination={true}
                        paginationPageSize={20}
                        className="ag-theme-quartz ag-theme-custom w-100"
                        containerStyle={{ height: "65dvh" }}
                        rowSelection="multiple"
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        suppressContextMenu
                        onSelectionChanged={(e) => {
                            const rowIds = e.api.getSelectedRows().map((row) => row.genericCategoryId);
                            handleUpdate("selectedCategoryRows", rowIds);
                        }}
                        onCellClicked={(e) => {
                            if (e.column.getColId() === "genericCategoryName")
                            setSelectedGenericCategoriesId(e.data.genericCategoryId);  
                        }}

                        components={{
                            baseCellRenderer: BaseCellRenderer,
                        }}
                    />
                </Col>
                <Col lg={6}>
                    <ImportWizard
                        title="Preferred Brands"
                        matchAgainst="Products"
                        display={importWizardPopup}
                        importAsync={(i) => importPreferredBrands(i)}
                        validateTable={(rows) => WholeTableValidation(rows)}
                        handleClose={() => { 
                            if (terminateWorkersRef.current) terminateWorkersRef.current();
                            setImportWizardPopup(false);
                        }}
                        finish={() => setImportWizardPopup(false)}
                        importPercentage={importPercentage}
                    />
                    <AgGridToolbar
                        search={true}
                        className="pb-1"
                        handleSubmit={(searchState, filterState) => {
                            genericCategoriesStocksGridRef.current?.api.clearRangeSelection();
                            handleUpdate("selectedCategoryRows", []); /*** */
                            genericCategoriesStocksGridRef.current?.api.setGridOption('quickFilterText', searchState?.keyword);                                            
                        }}
                        searchProps={
                            {totalRows: genericCategoriesStock.length, placeholder: "by Name", hideContains: true}
                        }
                        children={(
                            <div className="d-inline-flex gap-1">
                                { 
                                (preferredBrands.selectedgenericCategoriesStock != undefined && preferredBrands.selectedgenericCategoriesStock.length != 0) &&									
                                    <Z.Button variant="light" className="h-100" disabled={true} buttonText={preferredBrands.selectedgenericCategoriesStock.length+ " Selected"}/>
                                }                                    
                                <ZTooltip text={
                                    preferredBrands.selectedgenericCategoriesStock.length == 0? `Please select a row to delete`:
                                    preferredBrands.selectedgenericCategoriesStock.length == preferredBrands.pbsManufacturers.length ? `Delete all rows`:									  
                                    preferredBrands.selectedgenericCategoriesStock.length> 0 ? `Delete ${preferredBrands.selectedgenericCategoriesStock.length} rows` :                                                    
                                    "Please select a row to delete"}>
                                    <Z.Button icon={ZIconType.Delete} buttonSize="md"  onClick={() => setShowDeleteStock(true)} 
                                        disabled={preferredBrands.selectedgenericCategoriesStock.length < 1} />
                                </ZTooltip>
                                <ZTooltip text="Refresh">
                                    <Z.Button icon={ZIconType.Refresh} buttonSize="md" onClick={() => { dispatch(getGenericCategories({})).then((res) => {
                                            handleUpdate("mainGenericCategoriesTable", res.data);
                                            if (preferredBrands.stock.length > 0) genericCategoriesDataProsess();
                                            setGenericCategoriesLoading(true);
                                        })}}
                                    />
                                </ZTooltip>
                                 <ZTooltip text="Import From Spreadsheet">
                                    <Z.Button
                                        size="sm"
                                        className="btn-icon custom-btn-icon"
                                        onClick={user.permissions.preferredBrands.canImport ? ()=> setImportWizardPopup(true) : undefined}
                                        disabled={!user.permissions.preferredBrands.canCreate || !user.permissions.preferredBrands.canUpdate} 
                                        icon={ZIconType.Upload}
                                        iconSize="18px"
                                        style={{height: 40}}
                                        />
                                </ZTooltip>
                            </div>
                        )}
                    />
                    <AgGridReact
                        ref={genericCategoriesStocksGridRef}
                        columnDefs={getPreferredBrandsStockLevelConfig(genericCategoriesStock.length, ()=> updateGenericStockOrder())}
                        rowData={genericCategoriesStock}
                        pagination={true}
                        paginationPageSize={20}
                        className="ag-theme-quartz ag-theme-custom w-100"
                        containerStyle={{ height: "65dvh" }}
                        rowSelection="multiple"
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        suppressContextMenu
                        onSelectionChanged={(e) => {
                            const rowIds = e.api.getSelectedRows().map((row) => row.productId);
                            handleUpdate("selectedgenericCategoriesStock", rowIds);
                        }}
                        onCellClicked={(e) => {
                            if (e.column.getColId() === "genericCategoryName")
                            setSelectedGenericCategoriesId(e.data.genericCategoryId);  
                        }}

                        components={{
                            baseCellRenderer: BaseCellRenderer,
                        }}
                    />

                </Col>
            </Row>
            <Modal
                title="Confirm Delete"
                show={showDeleteCategory || showDeleteStock}
                buttonType="primary"
                buttonText={"Continue"}
                buttonCloseText="Cancel"
                handleSubmit={() => {
                    if(showDeleteCategory)
                        handleDeleteCategory();
                    else
                        handleDelete();
                    setShowDeleteCategory(false);
                    setShowDeleteStock(false);
                    genericCategoryTableRef.current?.api.deselectAll();

                }}
                handleClose={() => setShowDeleteCategory(false)}
                description={
                    <>
                        <p>You are about to delete {showDeleteCategory == true? preferredBrands.selectedCategoryRows.length: preferredBrands.selectedgenericCategoriesStock.length} selected {preferredBrands.selectedCategoryRows.length > 1 ||
                        preferredBrands.selectedgenericCategoriesStock.length > 1
                        ? "items": "item"}.</p>
                        <p>Are you sure you want to delete?</p>
                    </>
                }
            />
        </div>
    );
}